'use client'

/**
 * Trading context strip — Layer B of desk chrome.
 * Mode, feed, RT, and market freshness share one status vocabulary.
 * Identity (search / profile / theme) stays in the global navbar.
 */

import React, { useMemo } from 'react'
import { ReloadOutlined } from '@ant-design/icons'
import {
  DeskButton,
  DeskChassis,
  DeskChip,
  DeskSelect,
  DeskSwitch,
} from '@/shared/components/desk'
import {
  deskFeedMark,
  deskModeMark,
  deskRealtimeMark,
  type DeskStatusMark,
} from './deskStatus'
import styles from './SessionInstrumentStrip.module.css'

export type PathState = 'live' | 'connecting' | 'error' | 'offline'
export type RealtimeState = 'on' | 'connecting' | 'error' | 'off'

export interface SessionInstrumentStripProps {
  greeting: string
  planLabel: string
  simulated?: boolean
  developer?: boolean
  environment?: string | null
  connections?: ReadonlyArray<{ id: string; label: string }>
  selectedConnectionId?: string
  path: PathState
  realtime: RealtimeState
  clock: string
  asOf?: string
  marketStatus?: string | null
  autoRefresh: boolean
  refreshing: boolean
  density: 'standard' | 'compact'
  onRefresh: () => void
  onConnection?: (connectionId: string) => void
  onToggleAuto: (next: boolean) => void
  onDensity: (d: 'standard' | 'compact') => void
}

function contextMarks(input: {
  developer?: boolean
  simulated?: boolean
}): Array<{ id: string; label: string; tone: 'primary' | 'warning' | 'default' }> {
  const marks: Array<{ id: string; label: string; tone: 'primary' | 'warning' | 'default' }> = []
  if (input.developer) {
    marks.push({ id: 'developer', label: 'Developer', tone: 'primary' })
  }
  if (input.simulated) {
    marks.push({ id: 'simulated', label: 'Simulated', tone: 'warning' })
  }
  return marks
}

function clusterKindLabel(kind: DeskStatusMark['kind']): string {
  if (kind === 'rt') return 'RT'
  if (kind === 'feed') return 'Feed'
  if (kind === 'mode') return 'Mode'
  return 'Market'
}

function clusterValue(mark: DeskStatusMark, extra?: string | null): string {
  const raw = (extra || mark.label || '').trim()
  if (mark.kind === 'rt' && /^RT\s+/i.test(raw)) {
    return raw.replace(/^RT\s+/i, '')
  }
  if (mark.kind === 'feed' && /^Path\s+/i.test(raw)) {
    return raw.replace(/^Path\s+/i, '')
  }
  return raw
}

function StatusCluster({
  mark,
  extra,
}: {
  mark: DeskStatusMark
  extra?: string | null
}) {
  const value = clusterValue(mark, extra)
  return (
    <span
      className={styles.cluster}
      data-kind={mark.kind}
      data-tone={mark.tone}
      data-status={value}
      title={mark.title}
    >
      <small>{clusterKindLabel(mark.kind)}</small>
      <i aria-hidden />
      <b>{value}</b>
    </span>
  )
}

function clockFace(value: string): string {
  if (!value || value.startsWith('--')) return '—'
  return value.length >= 5 ? value.slice(0, 5) : value
}

export function SessionInstrumentStrip({
  greeting,
  planLabel,
  simulated = false,
  developer = false,
  environment = null,
  connections = [],
  selectedConnectionId,
  path,
  realtime,
  clock,
  asOf,
  marketStatus = null,
  autoRefresh,
  refreshing,
  density,
  onRefresh,
  onConnection,
  onToggleAuto,
  onDensity,
}: SessionInstrumentStripProps) {
  const mode = useMemo(() => deskModeMark(environment), [environment])
  const feed = deskFeedMark(path)
  const transport = deskRealtimeMark(realtime)
  const market = marketStatus
    ? ({
        kind: 'market' as const,
        label: marketStatus,
        tone:
          marketStatus === 'Delayed' || marketStatus === 'Age unknown'
            ? ('warn' as const)
            : marketStatus === 'Unavailable'
              ? ('error' as const)
              : ('ok' as const),
        title: `Market data ${marketStatus}`,
      } satisfies DeskStatusMark)
    : null
  const marks = useMemo(
    () => contextMarks({ developer, simulated }),
    [developer, simulated]
  )
  const accountOptions = useMemo(
    () =>
      connections.map((connection) => ({
        value: connection.id,
        label: connection.label,
      })),
    [connections]
  )
  const spoken = [mode?.title, feed.title, transport.silent ? null : transport.title, market?.title]
    .filter(Boolean)
    .join('. ')
  const asOfFace =
    typeof asOf === 'string' && asOf && asOf !== '—'
      ? asOf.length >= 5
        ? asOf.slice(0, 5)
        : asOf
      : null

  return (
    <DeskChassis>
      <header
        className={styles.strip}
        data-density={density}
        data-strip="session"
        aria-label={`${greeting} trading context`}
      >
        <div className={styles.body}>
          <div className={styles.context} aria-label="Trading environment">
            <div className={styles.contextTrail} role="list" aria-label="Desk context">
              {marks.map((mark, index) => (
                <React.Fragment key={mark.id}>
                  {index > 0 ? (
                    <span className={styles.trailSep} aria-hidden>
                      /
                    </span>
                  ) : null}
                  <span role="listitem">
                    <DeskChip tone={mark.tone} className={styles.mark} data-tone={mark.tone}>
                      {mark.label}
                    </DeskChip>
                  </span>
                </React.Fragment>
              ))}
              {marks.length > 0 ? (
                <span className={styles.trailSep} aria-hidden>
                  /
                </span>
              ) : null}
              <span className={styles.plan} role="listitem">
                {planLabel}
              </span>
            </div>
            {connections.length > 0 && onConnection ? (
              <DeskSelect
                className={styles.accountSelect}
                aria-label="Active trading account"
                placeholder="Account"
                value={selectedConnectionId || undefined}
                options={accountOptions}
                optionFilterProp="label"
                showSearch={connections.length > 5}
                popupMatchSelectWidth={false}
                onChange={onConnection}
              />
            ) : null}
          </div>

          <div className={styles.health} aria-label="Desk health">
            <p className={styles.healthLine} role="status" title={spoken} aria-label={spoken}>
              {mode ? <StatusCluster mark={mode} /> : null}
              <StatusCluster mark={feed} />
              {transport.silent ? null : <StatusCluster mark={transport} />}
              {market ? <StatusCluster mark={market} /> : null}
              <span className={styles.sep} aria-hidden>
                ·
              </span>
              <time className={styles.clock} dateTime={clock}>
                {clockFace(clock)}
              </time>
              {asOfFace && market?.tone === 'warn' ? (
                <span className={styles.asOf}>As of {asOfFace}</span>
              ) : null}
            </p>
          </div>

          <div className={styles.actions} aria-label="Desk controls">
            <DeskButton
              className={styles.refresh}
              icon={<ReloadOutlined />}
              loading={refreshing}
              onClick={onRefresh}
              aria-label={refreshing ? 'Refreshing session data' : 'Refresh session data'}
            />
            <label className={styles.auto}>
              <span>Auto</span>
              <DeskSwitch
                checked={autoRefresh}
                onChange={onToggleAuto}
                aria-label={
                  autoRefresh
                    ? 'Auto-refresh on, click to turn off'
                    : 'Auto-refresh off, click to turn on'
                }
              />
            </label>
            <div
              className={styles.density}
              role="radiogroup"
              aria-label="Display density"
            >
              <button
                type="button"
                role="radio"
                className={styles.densityOption}
                aria-checked={density === 'standard'}
                data-active={density === 'standard' ? 'true' : undefined}
                onClick={() => onDensity('standard')}
              >
                Standard
              </button>
              <button
                type="button"
                role="radio"
                className={styles.densityOption}
                aria-checked={density === 'compact'}
                data-active={density === 'compact' ? 'true' : undefined}
                onClick={() => onDensity('compact')}
              >
                Compact
              </button>
            </div>
          </div>
        </div>
      </header>
    </DeskChassis>
  )
}

export default SessionInstrumentStrip
